import React from 'react';
import { WxbButton } from '../Button/Button';
import type { WxbButtonProps } from '../Button/Button';
import { WxbDrawer } from '../Drawer/Drawer';
import type { WxbDrawerProps } from '../Drawer/Drawer';
import { WxbModal } from '../Modal/WxbModal';
import type { WxbModalProps } from '../Modal/WxbModal';
import './Form.css';

type JoinableClass = string | false | undefined;

const joinClasses = (...classes: JoinableClass[]) => classes.filter(Boolean).join(' ');

export type WxbFormLayout = 'vertical' | 'horizontal';
export type WxbFormGap = 'sm' | 'md' | 'lg';
export type WxbFormColumns = 1 | 2 | 3 | 4 | 'auto';
export type WxbFormFooterAlign = 'start' | 'end' | 'between';

export interface WxbFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  layout?: WxbFormLayout;
  gap?: WxbFormGap;
}

export const WxbForm = React.forwardRef<HTMLFormElement, WxbFormProps>(({
  layout = 'vertical',
  gap = 'md',
  className = '',
  children,
  ...props
}, ref) => (
  <form
    ref={ref}
    className={joinClasses('wxb-form', `wxb-form-${layout}`, `wxb-form-gap-${gap}`, className)}
    {...props}
  >
    {children}
  </form>
));

WxbForm.displayName = 'WxbForm';

export interface WxbFormGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: WxbFormColumns;
  gap?: WxbFormGap;
}

export const WxbFormGrid: React.FC<WxbFormGridProps> = ({
  columns = 'auto',
  gap = 'md',
  className = '',
  children,
  ...props
}) => (
  <div
    className={joinClasses('wxb-form-grid', `wxb-form-grid-${columns}`, `wxb-form-grid-gap-${gap}`, className)}
    {...props}
  >
    {children}
  </div>
);

export interface WxbFormSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  density?: 'standard' | 'compact';
}

export const WxbFormSection: React.FC<WxbFormSectionProps> = ({
  title,
  description,
  actions,
  density = 'standard',
  className = '',
  children,
  ...props
}) => (
  <section className={joinClasses('wxb-form-section', `wxb-form-section-${density}`, className)} {...props}>
    {(title || description || actions) && (
      <div className="wxb-form-section-header">
        <div className="wxb-form-section-copy">
          {title && <h3 className="wxb-form-section-title">{title}</h3>}
          {description && <p className="wxb-form-section-description">{description}</p>}
        </div>
        {actions && <div className="wxb-form-section-actions">{actions}</div>}
      </div>
    )}
    <div className="wxb-form-section-body">{children}</div>
  </section>
);

export interface WxbFormFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: WxbFormFooterAlign;
  border?: boolean;
}

export const WxbFormFooter: React.FC<WxbFormFooterProps> = ({
  align = 'end',
  border = false,
  className = '',
  children,
  ...props
}) => (
  <div
    className={joinClasses('wxb-form-footer', `wxb-form-footer-${align}`, border && 'wxb-form-footer-bordered', className)}
    {...props}
  >
    {children}
  </div>
);

export interface WxbValidationIssue {
  field?: React.ReactNode;
  message: React.ReactNode;
}

export interface WxbValidationSummaryProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  issues?: Array<WxbValidationIssue | React.ReactNode>;
  hideWhenEmpty?: boolean;
}

export const WxbValidationSummary: React.FC<WxbValidationSummaryProps> = ({
  title = '请先处理以下问题',
  issues = [],
  hideWhenEmpty = true,
  className = '',
  ...props
}) => {
  if (issues.length === 0 && hideWhenEmpty) return null;

  return (
    <div className={joinClasses('wxb-validation-summary', className)} role="alert" {...props}>
      <div className="wxb-validation-summary-title">{title}</div>
      {issues.length > 0 && (
        <ul className="wxb-validation-summary-list">
          {issues.map((issue, index) => {
            const isStructured = typeof issue === 'object'
              && issue !== null
              && 'message' in issue
              && !React.isValidElement(issue);
            const structuredIssue = isStructured ? issue as WxbValidationIssue : null;
            const message = structuredIssue ? structuredIssue.message : issue as React.ReactNode;

            return (
              <li key={index} className="wxb-validation-summary-item">
                {structuredIssue?.field && <span className="wxb-validation-summary-field">{structuredIssue.field}</span>}
                <span>{message}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

interface WxbFormChromeProps {
  formId?: string;
  children: React.ReactNode;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  submitText?: React.ReactNode;
  cancelText?: React.ReactNode;
  submitVariant?: WxbButtonProps['variant'];
  submitting?: boolean;
  submitDisabled?: boolean;
  footerExtra?: React.ReactNode;
}

const cssSafeFormId = (prefix: string, reactId: string): string => (
  `${prefix}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`
);

export interface WxbModalFormProps
  extends Omit<WxbModalProps, 'children' | 'footer' | 'onOk' | 'confirmLoading'>,
  WxbFormChromeProps {}

export const WxbModalForm: React.FC<WxbModalFormProps> = ({
  formId,
  children,
  onSubmit,
  submitText = '保存',
  cancelText = '取消',
  submitVariant = 'primary',
  submitting = false,
  submitDisabled = false,
  footerExtra,
  onCancel,
  ...modalProps
}) => {
  const generatedId = React.useId();
  const resolvedFormId = formId ?? cssSafeFormId('wxb-modal-form', generatedId);

  return (
    <WxbModal
      {...modalProps}
      onCancel={onCancel}
      footer={(
        <WxbFormFooter align={footerExtra ? 'between' : 'end'}>
          {footerExtra && <div className="wxb-form-footer-extra">{footerExtra}</div>}
          <div className="wxb-form-footer-actions">
            <WxbButton type="button" variant="ghost" onClick={onCancel as any}>
              {cancelText}
            </WxbButton>
            <WxbButton
              type="submit"
              form={resolvedFormId}
              variant={submitVariant}
              disabled={submitting || submitDisabled}
              aria-busy={submitting || undefined}
            >
              {submitting ? '处理中...' : submitText}
            </WxbButton>
          </div>
        </WxbFormFooter>
      )}
    >
      <WxbForm id={resolvedFormId} onSubmit={onSubmit}>
        {children}
      </WxbForm>
    </WxbModal>
  );
};

export interface WxbDrawerFormProps
  extends Omit<WxbDrawerProps, 'children' | 'footer'>,
  WxbFormChromeProps {}

export const WxbDrawerForm: React.FC<WxbDrawerFormProps> = ({
  formId,
  children,
  onSubmit,
  submitText = '保存',
  cancelText = '取消',
  submitVariant = 'primary',
  submitting = false,
  submitDisabled = false,
  footerExtra,
  onClose,
  ...drawerProps
}) => {
  const generatedId = React.useId();
  const resolvedFormId = formId ?? cssSafeFormId('wxb-drawer-form', generatedId);

  return (
    <WxbDrawer
      {...drawerProps}
      onClose={onClose}
      footer={(
        <WxbFormFooter align={footerExtra ? 'between' : 'end'}>
          {footerExtra && <div className="wxb-form-footer-extra">{footerExtra}</div>}
          <div className="wxb-form-footer-actions">
            <WxbButton type="button" variant="ghost" onClick={onClose as any}>
              {cancelText}
            </WxbButton>
            <WxbButton
              type="submit"
              form={resolvedFormId}
              variant={submitVariant}
              disabled={submitting || submitDisabled}
              aria-busy={submitting || undefined}
            >
              {submitting ? '处理中...' : submitText}
            </WxbButton>
          </div>
        </WxbFormFooter>
      )}
    >
      <WxbForm id={resolvedFormId} onSubmit={onSubmit}>
        {children}
      </WxbForm>
    </WxbDrawer>
  );
};
